import React, { useState } from 'react';
import Alert from './Alert';
import { unitLabel } from '../utils/format';

const UNITS = ['kg', 'l'];

export default function ProductModal({ product, onCancel, onSave }) {
  const isEdit = !!product;
  const [form, setForm] = useState({
    name: product?.name || '',
    sku: product?.sku || '',
    barcode: product?.barcode || '',
    category: product?.category || '',
    mrp: product?.mrp ?? '',
    price: product?.price ?? '',
    basePack: product?.base_pack ?? '',
    qty: product?.qty ?? 0,
    isLoose: product?.is_loose ?? false,
    unit: product?.unit ?? '',
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  function update(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  function toggleLoose(checked) {
    setForm((f) => ({
      ...f,
      isLoose: checked,
      unit: checked ? (f.unit || 'kg') : '',
      qty: checked ? f.qty : String(parseInt(parseFloat(f.qty) || 0)),
    }));
  }

  async function handleSubmit() {
    setError('');
    if (!form.name.trim() || !form.sku.trim() || !form.barcode.trim()) {
      setError('Name, SKU and barcode are required.');
      return;
    }
    if (!form.isLoose && !String(form.basePack).trim()) {
      setError('Base pack is required for packaged products.');
      return;
    }
    if (form.isLoose && !form.unit) {
      setError('Please select a unit (kg or l) for loose products.');
      return;
    }
    setSaving(true);
    try {
      await onSave({
        name: form.name.trim(),
        sku: form.sku.trim(),
        barcode: form.barcode.trim(),
        category: form.category.trim(),
        mrp: parseFloat(form.mrp) || 0,
        price: parseFloat(form.price) || 0,
        basePack: form.isLoose ? '' : form.basePack.trim(),
        qty: form.isLoose ? parseFloat(form.qty) || 0 : parseInt(form.qty, 10) || 0,
        isLoose: form.isLoose,
        unit: form.isLoose ? form.unit : null,
      });
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to save product.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">{isEdit ? 'Edit Product' : 'Add Product'}</div>
          <button className="btn btn-sm" onClick={onCancel}>
            <i className="ti ti-x" aria-hidden="true"></i>
          </button>
        </div>
        <div className="modal-body">
          <Alert type="danger" message={error} onClose={() => setError('')} />
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Product Name *</label>
              <input value={form.name} onChange={(e) => update('name', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">SKU *</label>
              <input value={form.sku} onChange={(e) => update('sku', e.target.value)} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Barcode *</label>
              <input value={form.barcode} onChange={(e) => update('barcode', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Category</label>
              <input value={form.category} onChange={(e) => update('category', e.target.value)} />
            </div>
          </div>
          <div className="form-row-3">
            <div className="form-group">
              <label className="form-label">MRP (₹) *</label>
              <input type="number" value={form.mrp} onChange={(e) => update('mrp', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Wholesale Price (₹) *</label>
              <input type="number" value={form.price} onChange={(e) => update('price', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">{isEdit ? 'Quantity' : 'Initial Quantity'}</label>
              <input
                type="number"
                step={form.isLoose ? '0.001' : '1'}
                min="0"
                value={form.qty}
                onChange={(e) => {
                  const v = e.target.value;
                  update('qty', form.isLoose ? v : v.replace(/\.\d*/, ''));
                }}
              />
            </div>
          </div>

          {/* Loose product toggle */}
          <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '0.75rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 500, fontSize: 13 }}>
              <input
                type="checkbox"
                checked={form.isLoose}
                onChange={(e) => toggleLoose(e.target.checked)}
                style={{ width: 16, height: 16 }}
              />
              Loose / Unpackaged Product
            </label>
          </div>

          {form.isLoose ? (
            <div className="form-group">
              <label className="form-label">Unit *</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {UNITS.map((u) => (
                  <label
                    key={u}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 5,
                      padding: '6px 14px',
                      border: `2px solid ${form.unit === u ? 'var(--primary, #1976d2)' : 'var(--border, #e0e0e0)'}`,
                      borderRadius: 6,
                      cursor: 'pointer',
                      fontWeight: form.unit === u ? 700 : 400,
                      background: form.unit === u ? 'var(--primary-light, #e3f2fd)' : '#fff',
                      fontSize: 13,
                    }}
                  >
                    <input
                      type="radio"
                      name="unit"
                      value={u}
                      checked={form.unit === u}
                      onChange={() => update('unit', u)}
                      style={{ display: 'none' }}
                    />
                    {unitLabel(u)}
                  </label>
                ))}
              </div>
            </div>
          ) : (
            <div className="form-group">
              <label className="form-label">Base Pack *</label>
              <input type="text" value={form.basePack} onChange={(e) => update('basePack', e.target.value)} placeholder="e.g. 12 pcs, 1 box" />
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn" onClick={onCancel}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
            <i className="ti ti-check" aria-hidden="true"></i>
            {saving ? 'Saving...' : isEdit ? 'Save changes' : 'Add product'}
          </button>
        </div>
      </div>
    </div>
  );
}
